import { FolderOpen, Utensils } from "lucide-react";

import type { CategoryPaginatedItem, ItemWithRelations } from "~/api-refs";

import { Label } from "../../ui/label";
import { MultiCombobox } from "../../ui/multi-combobox";

export function ApplyToCategoriesOrItemsForm({
  categories,
  items,
  values,
  setValues,
  setError,
  setFormChanged,
}: {
  values: { categories: string[]; items: string[] };
  categories: CategoryPaginatedItem[] | undefined;
  items: ItemWithRelations[] | undefined;
  setError: (error: string | null) => void;
  setValues: (values: { categories: string[]; items: string[] }) => void;
  setFormChanged?: (value: boolean) => void;
}) {
  return (
    <div className="mt-5 space-y-2">
      <Label>Apply to Categories or Items</Label>
      <MultiCombobox
        groups={["categories", "items"]}
        groupLabels={{
          categories: "Categories",
          items: "Items",
        }}
        items={{
          categories: (categories ?? []).map(
            (category: NonNullable<typeof categories>[number]) => ({
              label: category.name,
              value: category._id,
              component: (
                <div className="flex items-start gap-2">
                  <FolderOpen className="text-category size-5" />
                  <div className="space-y-1">
                    <div className="flex items-baseline gap-4">
                      <p className="text-foreground">{category.name}</p>
                      <p className="body-sm text-foreground">
                        {category.itemCount} item
                        {category.itemCount !== 1 && "s"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {category.menuRelations?.map(
                        (
                          menuRelation: (typeof category.menuRelations)[number]
                        ) => (
                          <div
                            className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                            key={menuRelation.menu._id}
                          >
                            {menuRelation.menu.name}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ),
            })
          ),
          items: (items ?? []).map(
            (item: NonNullable<typeof items>[number]) => ({
              label: item.name,
              value: item._id,
              component: (
                <div className="flex items-start gap-3">
                  <Utensils className="text-item size-5" />
                  <div className="w-full space-y-1">
                    <div className="flex w-full items-center justify-between gap-1">
                      <p className="text-foreground">{item.name}</p>
                      {item.modifierGroupsCount !== "0" && (
                        <p className="caption bg-muted text-muted-foreground rounded-full px-2 py-1">
                          {item.modifierGroupsCount} mod. group
                          {item.modifierGroupsCount !== "1" && "s"}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {item.categoryRelations?.map(
                        (
                          categoryRelation: (typeof item.categoryRelations)[number]
                        ) => (
                          <div
                            className="caption bg-category-bg text-category rounded-full px-2 py-1"
                            key={categoryRelation.category._id}
                          >
                            {categoryRelation.category.name}
                          </div>
                        )
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {item.categoryRelations
                        .flatMap(
                          (cr: (typeof item.categoryRelations)[number]) =>
                            cr.category.menuRelations
                        )
                        .filter(
                          (
                            menuRelation
                          ): menuRelation is NonNullable<typeof menuRelation> =>
                            Boolean(menuRelation)
                        )
                        .map((menuRelation) => (
                          <div
                            className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                            key={menuRelation.menu._id}
                          >
                            {menuRelation.menu.name}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ),
            })
          ),
        }}
        onValueChange={(selectedValues: {
          categories: string[];
          items: string[];
        }) => {
          setValues(selectedValues);
          setFormChanged?.(true);
          if (selectedValues.categories.length || selectedValues.items.length) {
            setError(null);
          }
        }}
        values={values}
      />
    </div>
  );
}
